import React, { Fragment, useEffect, useState } from "react";
import styled from "styled-components";

import useDevice from "@hooks/useDevice";
import { useIsAuth } from "@hooks/useIsAuth";
import { getStatisticsData } from "@selectors/statistic";
import { loadOrdersStatistic } from "@store/thunks/stats";
import { useAppDispatch, useAppSelector } from "@hooks/store";
import { Statistics, StatsTable } from "@components/organisms";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import type { FC } from "react";

const Portal: FC = () => {
  const isAuth = useIsAuth();
  const dispatch = useAppDispatch();

  const { data, dateRange, filters, sort } = useAppSelector(getStatisticsData);
  const { isMobile, isLaptop, isDesktop } = useDevice();
  const [isCollapsed, setIsCollapsed] = useState(false);

  useEffect(() => {
    if (!data.length && isAuth) dispatch(loadOrdersStatistic());
  }, [isAuth]);

  const onSectionClick = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <Wrapper>
      <Fragment>
        <StyledArrowIcon
          $top={10}
          $left={20}
          fontSize="large"
          $position="absolute"
          onClick={onSectionClick}
        />
        <CollapseWrapper
          $isCollapsed={isCollapsed}
          $height={isLaptop || isDesktop ? 40 : undefined}
          $minHeight="270px"
          $overflow={isCollapsed ? "hidden" : ""}
          $display="flex"
        >
          <Statistics
            filters={filters}
            statistic={data}
            dateRange={dateRange}
          />
        </CollapseWrapper>
      </Fragment>

      <CollapseWrapper
        $height={60}
        $isMobile={isMobile}
        $statisticCollapsed={isCollapsed}
      >
        <StatsTable
          sort={sort}
          statistic={data}
          filters={filters}
          dateRange={dateRange}
          isStatisticCollapsed={isCollapsed}
        />
      </CollapseWrapper>
    </Wrapper>
  );
};

const CollapseWrapper = styled.div<{
  $height?: number;
  $display?: string;
  $overflow?: string;
  $minHeight?: string;
  $statisticCollapsed?: boolean;
  $isCollapsed?: boolean;
  $isMobile?: boolean;
}>`
  position: relative;

  display: ${({ $display }) => $display && $display};
  justify-content: center;
  align-items: center;

  padding: ${({ $height, $isCollapsed }) => {
    if ($isCollapsed) return 0;

    return $height && "2px";
  }};

  height: ${({ $height, $isCollapsed, $statisticCollapsed }) => {
    if (!$isCollapsed && $statisticCollapsed) return "100%";
    if ($isCollapsed) return 0;

    return $height && `${$height}%`;
  }};

  overflow: ${({ $overflow }) => $overflow && "hidden"};

  margin-top: ${({ $statisticCollapsed, $isMobile }) =>
    $statisticCollapsed ? 30 : $isMobile ? 0 : 0}px;

  min-height: ${({ $minHeight, $isCollapsed }) => {
    return !$isCollapsed ? $minHeight : 0;
  }};

  width: 100%;

  transition: 0.4s ease;
  z-index: 3;
`;

const Wrapper = styled.div`
  width: 100%;
  height: 100%;

  display: flex;
  flex-direction: column;
`;

const StyledArrowIcon = styled(KeyboardArrowDownIcon)<{
  $top: number;
  $left: number;
  $position?: string;
}>`
  position: ${({ $position }) => ($position ? $position : "absolute")};

  left: ${({ $left }) => $left && $left}px;
  top: ${({ $top }) => $top && $top}px;

  cursor: pointer;
  z-index: 4;
`;

export default Portal;
